// A field that the form edits as a LIST but the entity stores as ONE STRING --
// e.g. an email's to / cc / bcc: several addresses in the form, one
// comma-separated string in the database. A spec opts in with
// `delimitedStringSeparator` (the separator written on save, e.g. ', ').
//
// The conversion happens at the only two boundaries:
//   - OPEN: setInputFieldsStartAndValue splits the stored string into `start`
//     and `value` (separate arrays), BEFORE anything compares them, so an
//     untouched field is not mistaken for an edited one.
//   - SAVE: getDirtyFieldsObject joins the list back into the string.
// In between, `value` is always an array: the widget (a `multiple` combobox)
// reads and writes it as one, and a cleared list field is `[]`, never null
// (getClearFieldValueFromStart).
//
// An entry is either a typed STRING or a picked ITEM (e.g. the whole contact
// row): the form keeps the picked row so other fields can follow it -- the
// email's Associated Contact follows the exact contact picked in To. Only the item's
// `itemValue` property (e.g. its email) ever leaves the form.

import type { IEntityMaskData } from '~/types/models'

// Both separators are accepted on the way IN, whatever the spec writes on the
// way out, so a stored "a@x.com; b@y.com" still opens as two entries.
const SPLIT_ON = /[,;]/

export function isDelimitedListField(ifield: IEntityMaskData): boolean {
  return ifield.delimitedStringSeparator !== undefined
}

// One entry as the string that is stored: a typed string as is, a picked item
// by its `itemValue` property.
export function listEntryToString(entry: unknown, itemValue?: string): string {
  if (entry !== null && typeof entry === 'object') {
    const picked = itemValue
      ? (entry as Record<string, unknown>)[itemValue]
      : undefined
    return picked == null ? '' : String(picked).trim()
  }
  return entry == null ? '' : String(entry).trim()
}

export function splitDelimitedString(
  stored: unknown,
  itemValue?: string
): string[] {
  if (Array.isArray(stored)) {
    return stored
      .map((entry) => listEntryToString(entry, itemValue))
      .filter(Boolean)
  }
  if (typeof stored !== 'string') {
    return []
  }
  return stored
    .split(SPLIT_ON)
    .map((entry) => entry.trim())
    .filter(Boolean)
}

export function joinDelimitedList(
  value: unknown,
  separator: string,
  itemValue?: string
): unknown {
  if (!Array.isArray(value)) {
    return value
  }
  return splitDelimitedString(value, itemValue).join(separator)
}
